import { useRef, useEffect, useState, useCallback, type KeyboardEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChatStore } from '../store/chat';
import { useFinancialModelV2Store } from '../store/financial-model-v2';
import clsx from 'clsx';

export function ChatPanel() {
  const isOpen = useChatStore((s) => s.isOpen);
  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const isLoadingHistory = useChatStore((s) => s.isLoadingHistory);
  const error = useChatStore((s) => s.error);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const clearChat = useChatStore((s) => s.clearChat);
  const loadHistory = useChatStore((s) => s.loadHistory);
  const togglePanel = useChatStore((s) => s.togglePanel);
  const modelId = useFinancialModelV2Store((s) => s.model?.id);

  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load chat history when panel opens
  useEffect(() => {
    if (isOpen && modelId) {
      loadHistory(modelId);
    }
  }, [isOpen, modelId, loadHistory]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || !modelId || isStreaming) return;
    setInput('');
    sendMessage(trimmed, modelId);
  }, [input, modelId, isStreaming, sendMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="w-[400px] h-full border-l border-gray-200 bg-white flex flex-col shrink-0">
        {/* Header */}
        <div className="h-10 px-3 flex items-center justify-between border-b border-gray-200 shrink-0">
          <span className="text-sm font-medium text-gray-700">AI Assistant</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => clearChat(modelId)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              title="Clear chat"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
            <button
              onClick={togglePanel}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              title="Close panel"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {isLoadingHistory ? (
            <div className="text-center text-gray-400 text-sm mt-8">
              Loading chat history...
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-400 text-sm mt-8">
              <p className="font-medium text-gray-500 mb-1">Ask about your model</p>
              <p>Revenue trends, what-if scenarios, sensitivity analysis...</p>
            </div>
          ) : null}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={clsx('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <div
                className={clsx(
                  'max-w-[85%] px-3 py-2 rounded-lg text-sm',
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white whitespace-pre-wrap'
                    : 'bg-gray-100 text-gray-800',
                )}
              >
                {msg.role === 'assistant' ? (
                  msg.content ? (
                    <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                  ) : isStreaming ? (
                    <span className="inline-block w-2 h-4 bg-gray-400 animate-pulse rounded-sm" />
                  ) : null
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}

          {error && (
            <div className="px-3 py-2 bg-red-50 text-red-600 text-xs rounded-lg">
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 p-3 shrink-0">
          <div className="flex gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your model..."
              rows={2}
              className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              disabled={isStreaming}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || !modelId || isStreaming}
              className={clsx(
                'self-end px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                input.trim() && modelId && !isStreaming
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed',
              )}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
              </svg>
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">
            {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Enter to send
          </p>
        </div>
      </div>
    </>
  );
}
